import { Injectable, inject } from '@angular/core';

import { AdministrationApiService } from './administration/administration-api.service';
import { AnalyticsApiService } from './analytics/analytics-api.service';
import { CartApiService } from './cart/cart-api.service';
import { ComboApiService } from './combo/combo-api.service';
import { CouponsApiService } from './coupons/coupons-api.service';
import { CustomFieldsApiService } from './custom-fields/custom-fields-api.service';
import { CustomerFeesApiService } from './customer-fees/customer-fees-api.service';
import { CustomerServiceApiService } from './customer-service/customer-service-api.service';
import { CustomersApiService } from './customers/customers-api.service';
import { DedupeApiService } from './dedupe/dedupe-api.service';
import { DepartmentsApiService } from './departments/departments-api.service';
import { DiscountsApiService } from './discounts/discounts-api.service';
import { DonationsApiService } from './donations/donations-api.service';
import { EmailStatusApiService } from './email-status/email-status-api.service';
import { EntitlementsApiService } from './entitlements/entitlements-api.service';
import { EntityApiService } from './entity/entity-api.service';
import { EventApiService } from './event/event-api.service';
import { FinanceApiService } from './finance/finance-api.service';
import { FormApiService } from './form/form-api.service';
import { GiftCardsApiService } from './gift-cards/gift-cards-api.service';
import { GlcodesApiService } from './glcodes/glcodes-api.service';
import { GroupsApiService } from './groups/groups-api.service';
import { HistoryApiService } from './history/history-api.service';
import { HouseholdApiService } from './household/household-api.service';
import { InventoryApiService } from './inventory/inventory-api.service';
import { InventoryB2CApiService } from './inventory-b2c/inventory-b2c-api.service';
import { LockApiService } from './lock/lock-api.service';
import { LoggerApiService } from './logger/logger-api.service';
import { MembershipApiService } from './membership/membership-api.service';
import { NavigationApiService } from './navigation/navigation-api.service';
import { OrdersApiService } from './orders/orders-api.service';
import { OrganizationsApiService } from './organizations/organizations-api.service';
import { PartnersApiService } from './partners/partners-api.service';
import { PassesApiService } from './passes/passes-api.service';
import { PermissionsApiService } from './permissions/permissions-api.service';
import { PhotosApiService } from './photos/photos-api.service';
import { PosApiService } from './pos/pos-api.service';
import { PriceApiService } from './price/price-api.service';
import { ProgramApiService } from './program/program-api.service';
import { RateCardsApiService } from './rate-cards/rate-cards-api.service';
import { RefundsApiService } from './refunds/refunds-api.service';
import { ReportingApiService } from './reporting/reporting-api.service';
import { ReservationsApiService } from './reservations/reservations-api.service';
import { ResourceApiService } from './resource/resource-api.service';
import { RolesApiService } from './roles/roles-api.service';
import { RulesEngineApiService } from './rules-engine/rules-engine-api.service';
import { SettlementsApiService } from './settlements/settlements-api.service';
import { SfdcApiService } from './sfdc/sfdc-api.service';
import { SubscriptionsApiService } from './subscriptions/subscriptions-api.service';
import { SystemApiService } from './system/system-api.service';
import { TenantApiService } from './tenant/tenant-api.service';
import { TerminalsApiService } from './terminals/terminals-api.service';
import { TextVariablesApiService } from './text-variables/text-variables-api.service';
import { ThemesApiService } from './themes/themes-api.service';
import { TicketsApiService } from './tickets/tickets-api.service';
import { UserApiService } from './user/user-api.service';
import { UserPreferencesApiService } from './user-preferences/user-preferences-api.service';
import { UserProxyApiService } from './user-proxy/user-proxy-api.service';
import { WalkinsApiService } from './walkins/walkins-api.service';

/**
 * ApiService facade (Angular 18).
 *
 * Replaces the AngularJS legacy facade at:
 *   apps/b2b-ui/common/services/api/api.service.js
 *
 * Strategy
 * --------
 * The legacy facade composes ~57 sub-services (one per backend domain). We
 * migrate them incrementally:
 *
 *   - **Migrated** sub-services are inject()'d directly as Angular 18 classes.
 *   - **Not yet migrated** sub-services are exposed as lazy getters that
 *     delegate to the AngularJS service of the same name through `$injector`.
 *
 * To migrate a sub-service in a future PR:
 *   1. Create `<feature>/<feature>-api.service.ts` (Angular 18, HttpClient).
 *   2. Replace the corresponding `get xxx()` getter with `readonly xxx = inject(XxxApiService)`.
 *   3. Add a spec, run typecheck, ship.
 */
const LEGACY_API_BASE = '/v1/api';

const LEGACY_SUB_SERVICES_TO_INIT = [] as const;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private bridgeCache: Record<string, unknown> = {};
  private readonly ajs = inject('$injector' as any) as {
    get(name: string): unknown;
  };

  constructor() {
    this.initLegacySubServices();
  }

  private bridge<T = unknown>(angularJsName: string): T {
    return (this.bridgeCache[angularJsName] ??= this.ajs.get(angularJsName)) as T;
  }

  private initLegacySubServices(): void {
    for (const name of LEGACY_SUB_SERVICES_TO_INIT) {
      const svc = this.bridge<{ init?: (endpoint: string) => unknown }>(name);
      if (svc && typeof svc.init === 'function') {
        svc.init(LEGACY_API_BASE);
      }
    }
  }

  // ============= Migrated sub-services (Angular 18) =============

  readonly user = inject(UserApiService);
  readonly system = inject(SystemApiService);
  readonly membership = inject(MembershipApiService);
  readonly program = inject(ProgramApiService);
  readonly orders = inject(OrdersApiService);
  readonly administration = inject(AdministrationApiService);
  readonly analytics = inject(AnalyticsApiService);
  readonly cart = inject(CartApiService);
  readonly combo = inject(ComboApiService);
  readonly coupons = inject(CouponsApiService);
  readonly customerFees = inject(CustomerFeesApiService);
  readonly customerService = inject(CustomerServiceApiService);
  readonly customers = inject(CustomersApiService);
  readonly customFields = inject(CustomFieldsApiService);
  readonly dedupe = inject(DedupeApiService);
  readonly departments = inject(DepartmentsApiService);
  readonly discounts = inject(DiscountsApiService);
  readonly donations = inject(DonationsApiService);
  readonly emailStatus = inject(EmailStatusApiService);
  readonly entitlements = inject(EntitlementsApiService);
  readonly entity = inject(EntityApiService);
  readonly event = inject(EventApiService);
  readonly finance = inject(FinanceApiService);
  readonly form = inject(FormApiService);
  readonly giftCards = inject(GiftCardsApiService);
  readonly glcodes = inject(GlcodesApiService);
  readonly groups = inject(GroupsApiService);
  readonly history = inject(HistoryApiService);
  readonly household = inject(HouseholdApiService);
  readonly inventory = inject(InventoryApiService);
  readonly inventoryB2C = inject(InventoryB2CApiService);
  readonly lock = inject(LockApiService);
  readonly logger = inject(LoggerApiService);
  readonly navigation = inject(NavigationApiService);
  readonly organizations = inject(OrganizationsApiService);
  readonly partners = inject(PartnersApiService);
  readonly passes = inject(PassesApiService);
  readonly permissions = inject(PermissionsApiService);
  readonly photos = inject(PhotosApiService);
  readonly pos = inject(PosApiService);
  readonly price = inject(PriceApiService);
  readonly rateCards = inject(RateCardsApiService);
  readonly refunds = inject(RefundsApiService);
  readonly reporting = inject(ReportingApiService);
  readonly reservations = inject(ReservationsApiService);
  readonly resource = inject(ResourceApiService);
  readonly roles = inject(RolesApiService);
  readonly rulesEngine = inject(RulesEngineApiService);
  readonly settlements = inject(SettlementsApiService);
  readonly sfdc = inject(SfdcApiService);
  readonly subscriptions = inject(SubscriptionsApiService);
  readonly tenant = inject(TenantApiService);
  readonly terminals = inject(TerminalsApiService);
  readonly textVariables = inject(TextVariablesApiService);
  readonly themes = inject(ThemesApiService);
  readonly tickets = inject(TicketsApiService);
  readonly userPreferences = inject(UserPreferencesApiService);
  readonly userProxy = inject(UserProxyApiService);
  readonly walkins = inject(WalkinsApiService);

  // ============= Bridges to AngularJS legacy =============
}
